import React, { useContext, useMemo, useState } from "react";
import { Button, EmptyState, Tag, cn } from "@jem-open/jem-ui";
import { FileSearch } from "lucide-react";
import PageContainer from "../../../themes/components/PageContainer";
import { AuthContext } from "../../../providers/AuthProvider";
import { useShiftLocationsApiService } from "../../time-attendance/services/api/ShiftLocationsApiService";
import { useListShiftSiteGroups } from "../../time-attendance/services/api/ShiftSiteGroupsCrudApiService";
import { getDateRangeForFilter } from "../../time-attendance/utils/dateRangeUtils";
import { LocationSummary } from "../../time-attendance/services/model/ShiftLocation";
import { ReportScope } from "../services/model/ReportScope";
import ScopePicker, { ScopeKind } from "../components/ScopePicker";
import { SearchableSelectOption } from "../components/SearchableSelect";
import ReportWizardBody from "../components/ReportWizardBody";

interface SiteEntry {
  id: string;
  name: string;
  region: string | null;
  clientName: string | null;
}

const ReportsPage = (): JSX.Element => {
  const auth = useContext(AuthContext);
  const [scopeKind, setScopeKind] = useState<ScopeKind>("site");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { from, to } = useMemo(() => getDateRangeForFilter("this-month"), []);

  // The dropdown only needs (id, name, site, site_name, region, client_name,
  // shift_configs). ``summary=true`` skips the per-location clock /
  // exception / hours queries on the backend; date_from/to are still sent
  // because the request type carries them but the slim path ignores them.
  const { data, isLoading, isError, error, refetch } =
    useShiftLocationsApiService({
      employer: auth?.employer?.id,
      date_from: from,
      date_to: to,
      summary: true,
    });

  const locations: LocationSummary[] = data?.results ?? [];

  // Regions are configured Site Groups (ShiftSiteGroup). The lightweight
  // CRUD list endpoint gives us {id, name, is_active, sites_count} — enough
  // to populate the picker. The backend resolves the group's rosters when
  // the report is generated.
  const { data: siteGroupsData, isLoading: isLoadingGroups } =
    useListShiftSiteGroups();

  const sites: SiteEntry[] = useMemo(() => {
    const seen = new Map<string, SiteEntry>();
    locations.forEach((loc) => {
      if (!loc.site || !loc.site_name) return;
      if (!seen.has(loc.site)) {
        seen.set(loc.site, {
          id: loc.site,
          name: loc.site_name,
          region: loc.region ?? null,
          clientName: loc.client_name ?? null,
        });
      }
    });
    return Array.from(seen.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [locations]);

  const siteOptions: SearchableSelectOption[] = useMemo(
    () =>
      sites.map((s) => ({
        value: s.id,
        label: s.name,
        sublabel: s.clientName ?? undefined,
        keywords: `${s.name} ${s.clientName ?? ""}`,
      })),
    [sites]
  );

  const rosterOptions: SearchableSelectOption[] = useMemo(
    () =>
      [...locations]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((loc) => ({
          value: loc.id,
          label: loc.name,
          sublabel: loc.site_name ?? loc.region ?? undefined,
          keywords: `${loc.name} ${loc.site_name ?? ""} ${loc.region ?? ""}`,
        })),
    [locations]
  );

  const siteGroups = useMemo(
    () => (siteGroupsData?.results ?? []).filter((g) => g.is_active),
    [siteGroupsData]
  );

  const regionOptions: SearchableSelectOption[] = useMemo(
    () =>
      [...siteGroups]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((g) => ({
          value: g.id,
          label: g.name,
          sublabel: g.sites_count === 1 ? "1 site" : `${g.sites_count} sites`,
          keywords: g.name,
        })),
    [siteGroups]
  );

  const hasScopeOptions = !isLoading && locations.length > 0;

  const scope: ReportScope | null = useMemo(() => {
    if (!selectedId) return null;
    if (scopeKind === "site") {
      const s = sites.find((x) => x.id === selectedId);
      if (!s) return null;
      return {
        kind: "site",
        id: s.id,
        label: s.name,
        region: s.region ?? undefined,
      };
    }
    if (scopeKind === "region") {
      const g = siteGroups.find((x) => x.id === selectedId);
      if (!g) return null;
      return { kind: "region", id: g.id, label: g.name };
    }
    const r = locations.find((x) => x.id === selectedId);
    if (!r) return null;
    return {
      kind: "roster",
      id: r.id,
      label: r.name,
      parentSite: r.site_name ?? undefined,
    };
  }, [scopeKind, selectedId, sites, locations, siteGroups]);

  return (
    <PageContainer>
      <div className={cn("pb-xl")}>
        <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
          Reports
        </h1>
        <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
          Generate a Planned vs Actuals, Timesheet, or Posting Sheet report for
          a site, roster, or region.
        </p>

        {(() => {
          if (isError) {
            return (
              <div className="mt-lg rounded-lg border border-red-200 bg-red-50 p-md">
                <p className="text-sm font-semibold text-red-700">
                  Couldn&apos;t load sites and rosters
                </p>
                <p className="text-xs text-red-600 mt-xxxs">
                  {error?.message ?? "Please try again."}
                </p>
                <div className="mt-sm">
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    Retry
                  </Button>
                </div>
              </div>
            );
          }
          if (!isLoading && !hasScopeOptions) {
            return (
              <div className="mt-lg">
                <EmptyState
                  icon={<FileSearch size={32} />}
                  title="No sites or rosters yet"
                  description="Add at least one site or roster in Time & Attendance before generating reports."
                />
              </div>
            );
          }
          return (
            <div className="mt-md grid gap-md grid-cols-1 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
              <div className="space-y-md">
                <ScopePicker
                  scopeKind={scopeKind}
                  onScopeKindChange={setScopeKind}
                  selectedId={selectedId}
                  onSelectedIdChange={setSelectedId}
                  siteOptions={siteOptions}
                  rosterOptions={rosterOptions}
                  regionOptions={regionOptions}
                  isLoading={
                    isLoading || (scopeKind === "region" && isLoadingGroups)
                  }
                />

                <div className="rounded-lg border border-greyscale-border bg-neutral-50 p-md">
                  <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption mb-xs">
                    Tip
                  </p>
                  <p className="text-sm text-greyscale-text-body">
                    You can also click{" "}
                    <span className="font-semibold text-greyscale-text-title">
                      Download Report
                    </span>{" "}
                    from any site or roster view in{" "}
                    <span className="font-semibold">Time &amp; Attendance</span>{" "}
                    to open this wizard pre-scoped.
                  </p>
                </div>
              </div>

              <div>
                {scope ? (
                  <div className="rounded-lg border border-greyscale-border bg-white p-lg">
                    <ReportWizardBody scope={scope} />
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-greyscale-border bg-neutral-50 p-xl hidden lg:flex items-center justify-center min-h-[320px]">
                    <div className="text-center max-w-[360px]">
                      <div className="inline-flex items-center justify-center rounded-full bg-secondary-pink-100 text-secondary-pink-900 h-12 w-12 mb-sm">
                        <FileSearch size={22} />
                      </div>
                      <p className="text-sm font-semibold text-greyscale-text-title">
                        Pick a {scopeKind} to continue
                      </p>
                      <p className="text-xs text-greyscale-text-caption mt-xxxs">
                        After you pick a scope, choose the report type and set
                        the filters.
                      </p>
                      <div className="flex items-center justify-center gap-xs mt-sm">
                        <Tag variant="drafted" size="sm">
                          Planned vs Actuals
                        </Tag>
                        <Tag variant="drafted" size="sm">
                          Timesheet
                        </Tag>
                        <Tag variant="drafted" size="sm">
                          Posting Sheet
                        </Tag>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </PageContainer>
  );
};

export default ReportsPage;
